import type { ServerSnapshotMessage } from "@xiuxian/game-protocol";

export type FormationRealmKey =
  | "lianqi"
  | "zhuji"
  | "jindan"
  | "yuanying"
  | "huashen";

export interface FormationLayerContext {
  isSelf: boolean;
  isMobile: boolean;
  distanceToSelf: number;
  cameraZoom: number;
  visiblePlayerCount: number;
  debugLayerLimit: number;
}

export const FORMATION_REALM_KEYS: readonly FormationRealmKey[] = [
  "lianqi",
  "zhuji",
  "jindan",
  "yuanying",
  "huashen",
] as const;
const FORMATION_REALM_LAYER_COUNTS = [1, 2, 2, 3, 3] as const;
const CROWDED_PLAYER_COUNT = 18;

export function getFormationRealmTier(level: number): number {
  if (!Number.isFinite(level) || level <= 1) {
    return 1;
  }
  return Math.max(1, Math.min(5, Math.ceil(level / 10)));
}

export function getFormationRealmKey(level: number): FormationRealmKey {
  return FORMATION_REALM_KEYS[getFormationRealmTier(level) - 1] ?? "lianqi";
}

export function getFormationRealmLayerCount(level: number): number {
  return FORMATION_REALM_LAYER_COUNTS[getFormationRealmTier(level) - 1] ?? 1;
}

export function getFormationLayerLimit(
  player: Pick<ServerSnapshotMessage["self"], "attackPhase" | "level">,
  context: FormationLayerContext,
): number {
  const realmTier = getFormationRealmTier(player.level);
  const realmLayerCount = getFormationRealmLayerCount(player.level);
  const debugLayerLimit = Math.max(0, Math.min(3, context.debugLayerLimit));
  if (debugLayerLimit === 0) {
    return 0;
  }

  const baseLimit = context.isMobile
    ? getMobileFormationLayerLimit(player.attackPhase, realmTier, context)
    : getDesktopFormationLayerLimit(player.attackPhase, realmTier, context);
  return Math.min(debugLayerLimit, realmLayerCount, baseLimit);
}

function getDesktopFormationLayerLimit(
  attackPhase: ServerSnapshotMessage["self"]["attackPhase"],
  realmTier: number,
  context: FormationLayerContext,
): number {
  if (context.isSelf || attackPhase === "windup") {
    return 3;
  }
  const nearDistance = 700 / Math.max(0.5, context.cameraZoom);
  const midDistance = 1200 / Math.max(0.5, context.cameraZoom);
  if (attackPhase === "recovery") {
    return context.distanceToSelf <= midDistance ? 2 : 1;
  }
  if (context.distanceToSelf > midDistance) {
    return realmTier >= 4 && context.visiblePlayerCount < CROWDED_PLAYER_COUNT
      ? 1
      : 0;
  }
  if (context.distanceToSelf > nearDistance) {
    return realmTier >= 4 ? 2 : 1;
  }
  if (realmTier >= 4) {
    return 3;
  }
  if (realmTier >= 3) {
    return context.visiblePlayerCount >= CROWDED_PLAYER_COUNT ? 1 : 2;
  }
  return 1;
}

function getMobileFormationLayerLimit(
  attackPhase: ServerSnapshotMessage["self"]["attackPhase"],
  realmTier: number,
  context: FormationLayerContext,
): number {
  if (context.isSelf || attackPhase === "windup") {
    return 3;
  }
  const nearDistance = 520 / Math.max(0.5, context.cameraZoom);
  const midDistance = 900 / Math.max(0.5, context.cameraZoom);
  if (attackPhase === "recovery") {
    return context.distanceToSelf <= nearDistance ? 2 : 1;
  }
  if (context.distanceToSelf > midDistance) {
    return 0;
  }
  if (context.distanceToSelf > nearDistance) {
    return realmTier >= 4 && context.visiblePlayerCount < CROWDED_PLAYER_COUNT
      ? 1
      : 0;
  }
  if (realmTier >= 4) {
    return 2;
  }
  return context.visiblePlayerCount >= CROWDED_PLAYER_COUNT ? 0 : 1;
}
